import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { TailSpin } from "react-loader-spinner";

import { getApi } from "../../utils/api";
import { API_REQUEST_ASSET_LIST } from "../../config/Endpoints";
import AssetRequestForm from "./AssetRequestForm";
import { capitalize, formatDate } from "../../utils/helpers";
import GenerateProgress from "./GenerateProgress";
import {
  Badge,
  ResponsiveDataTable,
} from "../../v2/components/ui/index.ts";
import { EmptyStateMessage } from "../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import { FiEye } from "react-icons/fi";
import styles from "./it-requests-list.module.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40];

const AssetRequestList = ({ title, description, onTotalRequestChange }) => {
  const [employeeId, setEmployeeId] = useState("");
  const [assetList, setAssetList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [openProgressIndexes, setOpenProgressIndexes] = useState([]);
  const [assetProgressData, setAssetProgressData] = useState({});
  const [selectedIndex, setSelectedIndex] = useState();
  const accessToken = Cookies.get("accessToken");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRequest, setTotalRequest] = useState(0);

  const handleOpenProgress = (index, item) => {
    // Check if the index is already in openProgressIndexes
    if (!openProgressIndexes.includes(index)) {
      setAssetProgressData(item);
      setIsProgressOpen(true);
      setSelectedIndex(index);

      setOpenProgressIndexes([openProgressIndexes, index]);
    }
  };

  const handleCloseProgress = (index) => {
    setAssetProgressData({});
    setIsProgressOpen(false);
    setSelectedIndex();
    setOpenProgressIndexes(openProgressIndexes.filter((idx) => idx !== index));
  };

  const tableData = useMemo(
    () =>
      filteredItems.map((item, index) => ({
        assetType: item.assestType,
        reason: item.reason,
        requestedDate: formatDate(item.requestDate),
        status: item.empName,
        _original: item,
        _index: index,
      })) || [],
    [filteredItems]
  );

  const columns = useMemo(
    () => [
      {
        id: "assetType",
        header: "Asset Type",
        accessor: "assetType",
        sortable: true,
        getSortValue: (row) => String(row.assetType ?? ""),
      },
      {
        id: "reason",
        header: "Reason",
        accessor: "reason",
        sortable: true,
        getSortValue: (row) => String(row.reason ?? ""),
        truncate: true,
        getTitleText: (row) => String(row.reason ?? ""),
      },
      {
        id: "requestedDate",
        header: "Requested Date",
        accessor: "requestedDate",
        sortable: true,
        getSortValue: (row) => new Date(row._original.requestDate).getTime(),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        getSortValue: (row) => {
          const status = row._original.trackingStatus;
          return status === "Approved1" || status === "Approved2"
            ? "In-Progress"
            : capitalize(status);
        },
        render: (row) => {
          const status = row._original.trackingStatus;

          const variant =
            status === "Approved1" || status === "Approved2"
              ? "neutral"
              : status === "Rejected"
                ? "danger"
                : status === "pending"
                  ? "warning"
                  : "success";

          const labelText =
            status === "Approved1" || status === "Approved2"
              ? "In-Progress"
              : capitalize(status);

          return (
            <Badge variant={variant} >
              {labelText}
            </Badge>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => {
          const index = row._index;
          const isOpen = openProgressIndexes.includes(index);
          return (

            <button
              type="button"
              className="action-btn action-btn--view"
              onClick={() => handleOpenProgress(index, row._original)}
            >
              <FiEye size={14} />
              View
            </button>
          );
        },
      },
    ],
    [openProgressIndexes]
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    fetchList();
  }, [currentPage, limit]);

  const handleLimitChange = (nextLimit) => {
    setLimit(nextLimit);
    setCurrentPage(1);
  };

  const fetchList = () => {
    setIsLoading(true);
    getApi(
      API_REQUEST_ASSET_LIST + `?pageSize=${limit}&page=${currentPage}`,
      accessToken
    )
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          setAssetList(res.data.data);
          setFilteredItems(res.data.data);
          setTotalRequest(res.data.totalElements);
          setTotalPages(res.data.totalPages);
          if (typeof onTotalRequestChange === "function") {
            onTotalRequestChange(res.data.totalElements);
          }
        }
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isProgressOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isProgressOpen]);

  return (
    <>
      <div className={styles.listRoot}>
        {isLoading ? (
          <div className={styles.loadingRow}>
            <TailSpin
              height="60"
              width="60"
              color="var(--color-accent-600, #2563eb)"
              ariaLabel="loading-asset-requests"
              visible
            />
          </div>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            data={tableData}
            aria-label="Asset Requests"
            pagination
            page={currentPage}
            pageSize={limit}
            totalItems={totalRequest}
            onPageChange={handlePageChange}
            onPageSizeChange={handleLimitChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalLabel={
              <span className={styles.totalLabel}>
                Total Asset Requests: {totalRequest ?? 0}
              </span>
            }
            emptyState={
              <EmptyStateMessage message="No Records Available." />
            }
          />
        )}

        <div
          className="modal fade"
          id="staticBackdropAssetRequest"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <AssetRequestForm />
            </div>
          </div>
        </div>
      </div>

      {isProgressOpen && (
        <>
          <div
            className="sidebar-backdrop"
            onClick={() => handleCloseProgress(selectedIndex)}
          ></div>
          <aside className="right-sidebar asset-request-progress-sidebar">
            <div
              className="mb-4 page-heading"
              onClick={() => handleCloseProgress(selectedIndex)}
            >
              <h1>{assetProgressData?.assestType}</h1>
              <span className="close_icon">&#10005;</span>
            </div>
            <div className="detail">
              <h5>{assetProgressData.employeeName}</h5>
              <h6>Status: {assetProgressData.trackingStatus}</h6>
            </div>

            <ul className="track-progress">
              <li className="approved">
                <h6>Requested</h6>
                <p>
                  <small>15:30, {assetProgressData.requestDate}</small>
                </p>
                <p>{assetProgressData.reason}</p>
              </li>
              {GenerateProgress(assetProgressData)}
            </ul>
          </aside>
        </>
      )}
    </>
  );
};

export default AssetRequestList;
